/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * ServiceControlledFeatureResolver — resolves a SERVICE_CONTROLLED feature from
 * its declared source, applying the fallback precedence:
 *
 *   1. Fresh source state (DeviceFeatures, NPVR, …) — if non-null, use it and
 *      persist it as the latest known value.
 *   2. Persisted/runtime cached state (IndexedDB-hydrated) — used when the
 *      source has no fresh value.
 *   3. Safe default — `serviceFeature.defaultEnabled` if set, else `false`.
 *
 * Adding a new source means registering another provider here; the resolver
 * algorithm and `FeatureActivation` stay untouched.
 */

import { DeviceFeatureSource } from "./sources/DeviceFeatureSource";
import { NpvrFeatureSource } from "./sources/NpvrFeatureSource";
import { ServiceControlledFeatureState } from "./ServiceControlledFeatureState";
import { FeatureServiceSource } from "./types";
import type { Feature, TServiceFeatureMapping } from "./types";
import type { IServiceControlledFeatureProvider } from "../interfaces";

export class ServiceControlledFeatureResolver {
  private readonly mProviders: Map<
    FeatureServiceSource,
    IServiceControlledFeatureProvider
  >;

  public constructor() {
    this.mProviders = new Map<
      FeatureServiceSource,
      IServiceControlledFeatureProvider
    >([
      [FeatureServiceSource.DEVICE_FEATURES, new DeviceFeatureSource()],
      [FeatureServiceSource.NPVR, new NpvrFeatureSource()]
    ]);
  }

  public resolve(feature: Feature, mapping: TServiceFeatureMapping): boolean {
    const state = ServiceControlledFeatureState.getInstance();
    const provider = this.mProviders.get(mapping.source);

    if (provider !== undefined) {
      const fresh: boolean | null = provider.getFeatureState(mapping);
      if (fresh !== null) {
        state.setState(feature, fresh);
        return fresh;
      }
    }

    const cached: boolean | null = state.getState(feature);
    if (cached !== null) {
      return cached;
    }

    return mapping.defaultEnabled === true;
  }
}
